import { cn } from '../../utils';
import type { QuickAction } from '../../types';
import styles from './QuickActions.module.css';

export interface QuickActionsProps {
  actions: QuickAction[];
  /** Loading state - shows placeholder skeletons */
  isLoading?: boolean;
  /** Processing state - disables all buttons */
  isProcessing?: boolean;
  onActionClick?: (action: QuickAction) => void;
  /** Optional small heading rendered above the row of buttons */
  heading?: string;
  className?: string;
}

export function QuickActions({
  actions,
  isLoading = false,
  isProcessing = false,
  onActionClick,
  heading,
  className,
}: QuickActionsProps) {
  if (isLoading) {
    return (
      <div className={cn(styles.wrapper, className)}>
        {heading && <div className={styles.heading}>{heading}</div>}
        <div className={cn(styles.container, styles.visible)}>
          <span className={styles.skeleton} />
          <span className={styles.skeleton} />
          <span className={styles.skeleton} />
        </div>
      </div>
    );
  }

  if (actions.length === 0) return null;

  return (
    <div className={cn(styles.wrapper, className)}>
      {heading && <div className={styles.heading}>{heading}</div>}
      <div className={cn(styles.container, styles.visible)}>
        {actions.map((action, index) => {
        const actionId = `${action.label}-${action.search_query ?? action.form_id ?? ''}-${index}`;

        // A persistent action carries its own colors: render as a filled chip
        // overriding the default outlined quick-action styling.
        const customColors = action.bg_color || action.text_color;
        const customStyle = customColors
          ? {
              backgroundColor: action.bg_color ?? undefined,
              borderColor: action.bg_color ?? undefined,
              color: action.text_color ?? undefined,
            }
          : undefined;

        return (
          <button
            key={actionId}
            onClick={() => !isProcessing && onActionClick?.(action)}
            disabled={isProcessing}
            className={cn(
              styles.actionButton,
              isProcessing && styles.disabled,
              styles.visible
            )}
            style={customStyle}
            type="button"
          >
            {action.label}
          </button>
        );
        })}
      </div>
    </div>
  );
}
